import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_ORIGIN || "http://localhost:4000";

function formatDuration(totalSeconds) {
  if (totalSeconds == null || isNaN(Number(totalSeconds))) return "—";
  const s = Math.floor(Number(totalSeconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}

function formatStorage(kb) {
  if (kb == null || isNaN(Number(kb))) return "—";
  const n = Number(kb);
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} GB`;
  if (n >= 1024) return `${(n / 1024).toFixed(2)} MB`;
  return `${n.toFixed(2)} KB`;
}

function getMetricConsumed(metrics, path) {
  const m = metrics.find(r => r.metric_path === path);
  return m ? m.consumed : null;
}

function formatDate(value) {
  if (!value || value === "N/A") return value || "—";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false
    });
  } catch {
    return value;
  }
}

const DATACENTER_LABELS = { in: "India", sea: "Singapore", eu: "Europe", us: "US" };

function App() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("usage");

  // Usage Metrics tab
  const [query, setQuery] = useState("");
  const [workspaces, setWorkspaces] = useState([]);
  const [selected, setSelected] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [additionalMetrics, setAdditionalMetrics] = useState(null);

  // Workspace Creation tab
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [creationData, setCreationData] = useState([]);
  const [creationLoading, setCreationLoading] = useState(false);

  useEffect(() => {
    fetch(API + "/api/me", { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => setUser(data))
      .catch(()=>setUser(null));
  }, []);

  function login() {
    window.location.href = API + "/auth/login";
  }

async function search() {
  if (!user) {
    alert("Please login first to search workspaces.");
    return;
  }

  try {
    const res = await fetch(`${API}/api/workspaces?query=${encodeURIComponent(query)}`, { credentials: "include" });
    if (res.status === 401) {
      setUser(null);
      alert("Unauthorized: Please login first");
      return;
    }
    if (!res.ok) {
      const err = await res.text();
      alert(err || "Failed to fetch workspaces");
      return;
    }
    const data = await res.json();
    setWorkspaces(data);
  } catch (err) {
    console.error(err);
    alert("Error connecting to server");
  }
}

 async function downloadCSV() {
    if (!selected) return;
    window.location.href = `${API}/api/workspace/${selected.workspace_id}/csv`;
  }

async function fetchCreationData() {
  if (!user) { alert("Please login first."); return; }
  setCreationLoading(true);
  setCreationData([]);
  try {
    const params = new URLSearchParams();
    if (fromDate) params.set("from", fromDate);
    if (toDate)   params.set("to",   toDate);
    const res = await fetch(`${API}/api/workspaces/created?${params}`, { credentials: "include" });
    if (res.status === 401) { setUser(null); alert("Unauthorized: Please login first"); return; }
    if (!res.ok) { alert("Failed to fetch workspace creation data"); return; }
    setCreationData(await res.json());
  } catch (err) {
    console.error(err);
    alert("Error fetching workspace creation data");
  } finally {
    setCreationLoading(false);
  }
}

async function selectWorkspace(ws) {
  if (!user) {
    alert("Please login first to select a workspace.");
    return;
  }

  setSelected(ws);
  setAdditionalMetrics(null);
  try {
    const res = await fetch(`${API}/api/workspace/${ws.workspace_id}`, { credentials: "include" });
    if (!res.ok) {
      const err = await res.json();
      alert(err.error || "Failed to fetch workspace details");
      return;
    }
    const data = await res.json();
    setMetrics(data.metrics || []);
    setAdditionalMetrics(data.additionalMetrics || null);
  } catch (err) {
    console.error(err);
    alert("Error fetching workspace details");
  }
}

 return (
  <div style={{ padding: "40px", maxWidth: "1200px", margin: "0 auto", fontFamily: "Inter, sans-serif", color: "#222" }}>
    <h1 style={{ textAlign: "center", marginBottom: "30px" }}>Workspace Usage Dashboard</h1>

    {/* Login / User Section */}
    <div style={{
      background: "white", padding: "20px", borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)", marginBottom: "25px",
      display: "flex", alignItems: "center",
      justifyContent: user ? "space-between" : "center",
      height: user ? "auto" : "200px",
    }}>
      {user ? (
        <div style={{ fontSize: "16px" }}><strong>Welcome, {user.email}</strong></div>
      ) : (
        <button onClick={login} style={{
          background: "#0078D4", border: "none", padding: "14px 28px", color: "white",
          borderRadius: "8px", cursor: "pointer", fontSize: "18px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          Login with Microsoft
        </button>
      )}
    </div>

    {/* Tab Bar */}
    <div style={{ display: "flex", borderBottom: "2px solid #e0e0e0", marginBottom: "25px" }}>
      {[{ key: "usage", label: "Usage Metrics" }, { key: "creation", label: "Workspace Creation" }].map(({ key, label }) => (
        <button key={key} onClick={() => setActiveTab(key)} style={{
          padding: "10px 24px", border: "none", background: "none", cursor: "pointer",
          fontSize: "15px", fontWeight: activeTab === key ? 700 : 400,
          color: activeTab === key ? "#1F4E79" : "#666",
          borderBottom: activeTab === key ? "3px solid #1F4E79" : "3px solid transparent",
          marginBottom: "-2px", transition: "color 0.15s",
        }}>{label}</button>
      ))}
    </div>

    {/* ── Tab 1: Usage Metrics ── */}
    {activeTab === "usage" && (
      <>
        {/* Search Section */}
        <div style={{ background: "white", padding: "20px", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", marginBottom: "25px" }}>
          <div style={{ display: "flex", gap: "12px" }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search workspace name..."
              style={{ flex: 1, padding: "12px 16px", borderRadius: "8px", border: "1px solid #ccc", fontSize: "15px" }}
            />
            <button onClick={search} style={{ background: "#222", color: "white", padding: "12px 22px", borderRadius: "8px", border: "none", cursor: "pointer" }}>
              Search
            </button>
          </div>
          <div style={{ marginTop: "20px" }}>
            {workspaces.length === 0 ? (
              <p style={{ opacity: 0.6 }}>No results</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "15px" }}>
                {workspaces.map((w) => (
                  <div key={w.workspace_id} onClick={() => selectWorkspace(w)} style={{
                    padding: "15px", background: "white", borderRadius: "10px",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.07)", cursor: "pointer",
                  }}>
                    <div style={{ fontWeight: 600 }}>{w.workspace_name}</div>
                    <div style={{ fontSize: "13px", opacity: 0.6 }}>{w.workspace_id}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Workspace Details */}
        {selected && (
          <div style={{ background: "white", padding: "25px", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
            <h2 style={{ marginBottom: "10px" }}>Workspace Details</h2>
            <p><b>Name:</b> {selected.workspace_name}</p>
            <p><b>ID:</b> {selected.workspace_id}</p>
            <p><b>Created:</b> {formatDate(selected.created_date)}</p>
            <p><b>End:</b> {formatDate(selected.end_date)}</p>
            <p><b>Type:</b> {selected.type}</p>

            <button onClick={downloadCSV} style={{ marginTop: "10px", background: "#009688", color: "white", padding: "10px 18px", borderRadius: "8px", border: "none", cursor: "pointer" }}>
              Download metrics as CSV
            </button>

            {/* Usage Overview */}
            {additionalMetrics && (
              <div style={{ marginTop: "30px" }}>
                <h3 style={{ marginBottom: "14px" }}>Usage Overview</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "14px", marginBottom: "28px" }}>
                  {[
                    { label: "Active Users",        value: additionalMetrics.active_user_count },
                    { label: "Studies Published",   value: additionalMetrics.study_count },
                    { label: "Reports Generated",   value: additionalMetrics.report_count },
                    { label: "Copilot Queries",     value: getMetricConsumed(metrics, "copilet_queries") ?? "—" },
                    { label: "Transcript Consumed", value: formatDuration((getMetricConsumed(metrics, "live_hours_transcript_translate.transcript") ?? 0) + (getMetricConsumed(metrics, "live_hours_transcript_translate.live_hours") ?? 0)) },
                    { label: "Storage Consumed",    value: formatStorage(getMetricConsumed(metrics, "storage")) },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: "#f0f7ff", border: "1px solid #cce0f5", padding: "20px 16px", borderRadius: "10px", textAlign: "center" }}>
                      <div style={{ fontSize: "34px", fontWeight: 700, color: "#1F4E79" }}>{value ?? "—"}</div>
                      <div style={{ fontSize: "12px", color: "#555", marginTop: "5px" }}>{label}</div>
                    </div>
                  ))}
                </div>

                {additionalMetrics.user_details?.length > 0 && (
                  <>
                    <h3 style={{ marginBottom: "10px" }}>Active Users</h3>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#f7f7f7", textAlign: "left" }}>
                          {["#", "Name", "Email", "Role", "Last Login"].map(h => (
                            <th key={h} style={{ padding: "10px", fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {additionalMetrics.user_details.map((u, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                            <td style={{ padding: "10px" }}>{i + 1}</td>
                            <td style={{ padding: "10px" }}>{u.name}</td>
                            <td style={{ padding: "10px" }}>{u.email}</td>
                            <td style={{ padding: "10px" }}>{u.role}</td>
                            <td style={{ padding: "10px" }}>{formatDate(u.last_login)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}

            <h3 style={{ marginTop: "25px" }}>Metrics</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
              <thead>
                <tr style={{ background: "#f7f7f7", textAlign: "left" }}>
                  <th style={{ padding: "10px" }}>Readable</th>
                  <th>Path</th>
                  <th>Limit</th>
                  <th>Consumed</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((m, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: "10px" }}>{m.readable_metric}</td>
                    <td>{m.metric_path}</td>
                    <td>{m.limit}</td>
                    <td>{m.consumed}</td>
                    <td>{m.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )}

    {/* ── Tab 2: Workspace Creation ── */}
    {activeTab === "creation" && (
      <div style={{ background: "white", padding: "25px", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
        <h2 style={{ marginBottom: "20px" }}>Workspace Creation</h2>

        {/* Date range filter */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
          <label style={{ fontWeight: 600 }}>From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #ccc", fontSize: "14px" }} />
          <label style={{ fontWeight: 600 }}>To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #ccc", fontSize: "14px" }} />
          <button onClick={fetchCreationData} disabled={creationLoading} style={{
            background: creationLoading ? "#aaa" : "#1F4E79", color: "white",
            padding: "8px 22px", borderRadius: "8px", border: "none",
            cursor: creationLoading ? "not-allowed" : "pointer", fontSize: "14px",
          }}>
            {creationLoading ? "Fetching…" : "Fetch"}
          </button>
          {!creationLoading && creationData.length > 0 && (
            <span style={{ fontSize: "13px", color: "#555" }}>{creationData.length} workspace(s)</span>
          )}
        </div>

        {creationLoading && (
          <p style={{ color: "#555", fontStyle: "italic" }}>Fetching workspaces and resolving owners… this may take a moment.</p>
        )}

        {!creationLoading && creationData.length === 0 && (
          <p style={{ opacity: 0.6 }}>No workspaces found. Adjust the date range and click Fetch.</p>
        )}

        {!creationLoading && creationData.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
            <thead>
              <tr style={{ background: "#f7f7f7", textAlign: "left" }}>
                {["#", "Workspace Name", "Data Center", "Owner Name", "Owner Email", "Subscription", "Created"].map(h => (
                  <th key={h} style={{ padding: "10px", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {creationData.map((ws, i) => (
                <tr key={ws.workspace_id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "10px" }}>{i + 1}</td>
                  <td style={{ padding: "10px" }}>{ws.workspace_name}</td>
                  <td style={{ padding: "10px" }}>{DATACENTER_LABELS[ws.datacenter] || ws.datacenter || "—"}</td>
                  <td style={{ padding: "10px" }}>{ws.owner_name}</td>
                  <td style={{ padding: "10px" }}>{ws.owner_email}</td>
                  <td style={{ padding: "10px" }}>{ws.subscription_type}</td>
                  <td style={{ padding: "10px" }}>{formatDate(ws.created_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    )}
  </div>
);

}

export default App;
